export const resolveImageRevisionUrl = (image, revisionId) => {
  if (!image || !revisionId || !image.revisions?.length) return image?.url;
  const revision = image.revisions.find(
    (r) => String(r._id) === String(revisionId)
  );
  return revision?.url || image.url;
};

export const getCommentId = (comment) => comment?.id ?? comment?._id;

export const getUploadKey = (imageId, commentId) => `${imageId}:${commentId}`;

/** Editor response revision linked to a specific agent comment. */
export const findResponseRevision = (image, comment) => {
  if (!image?.revisions?.length || !comment) return null;

  if (comment.responseRevisionId) {
    const linked = image.revisions.find(
      (r) => String(r._id) === String(comment.responseRevisionId)
    );
    if (linked) return linked;
  }

  return null;
};

export const isCommentAddressed = (
  image,
  comment,
  addressedUploads,
  imageId
) => {
  const commentId = getCommentId(comment);
  if (commentId == null) return false;
  if (addressedUploads[getUploadKey(imageId, commentId)]) return true;
  return !!findResponseRevision(image, comment);
};

const enrichCommentWithResponse = (
  comment,
  image,
  addressedUploads,
  imageId
) => {
  const commentId = getCommentId(comment);
  const uploadInfo =
    commentId != null
      ? addressedUploads[getUploadKey(imageId, commentId)]
      : null;
  const responseRevision = findResponseRevision(image, comment);
  const responseRevisionUrl =
    uploadInfo?.url || responseRevision?.url || comment.responseRevisionUrl;
  const requestedRevisionUrl =
    comment.revisionId && image
      ? resolveImageRevisionUrl(image, comment.revisionId)
      : null;
  return {
    ...comment,
    ...(requestedRevisionUrl ? { requestedRevisionUrl } : {}),
    ...(responseRevisionUrl ? { responseRevisionUrl } : {}),
  };
};

export const withRevisionCommentStatuses = (
  comments,
  image,
  addressedUploads,
  imageId
) =>
  (comments || []).map((comment) => {
    if (comment.status === "revisionCompleted") {
      return enrichCommentWithResponse(
        comment,
        image,
        addressedUploads,
        imageId
      );
    }
    const addressed = isCommentAddressed(
      image,
      comment,
      addressedUploads,
      imageId
    );
    let enriched = comment;
    if (addressed) {
      enriched = { ...comment, status: "editorUploaded" };
    } else if (
      comment.responseRevisionId &&
      comment.status === "revisionInProgress"
    ) {
      enriched = { ...comment, status: "editorUploaded" };
    } else {
      enriched = {
        ...comment,
        status:
          comment.status === "revisionInProgress" ||
          comment.status === "editorUploaded"
            ? comment.status
            : "revisionSent",
      };
    }
    return enrichCommentWithResponse(enriched, image, addressedUploads, imageId);
  });

export const buildRevisionRequestItems = (
  commentedRawPhotos,
  rawImages,
  itemImages,
  addressedUploads
) => {
  const items = [];

  for (const entry of commentedRawPhotos || []) {
    const img =
      rawImages.find((r) => r.id === entry.imageId) ||
      itemImages?.find((r) => r.id === entry.imageId);
    if (!img) continue;

    const comments = (entry.comments || []).filter(
      (c) => getCommentId(c) != null && c.revisionId
    );
    if (!comments.length) continue;

    const commentsWithStatus = withRevisionCommentStatuses(
      comments,
      img,
      addressedUploads,
      entry.imageId
    );

    const pendingComments = comments.filter(
      (c) =>
        isRevisionCommentPending(c) &&
        !isCommentAddressed(img, c, addressedUploads, entry.imageId)
    );
    const sortByNewest = (a, b) =>
      new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    const latestPending = [...pendingComments].sort(sortByNewest)[0];
    const latestComment = [...comments].sort(sortByNewest)[0];
    const displayComment = latestPending || latestComment;
    const addressed = pendingComments.length === 0;

    const activeCommentId = latestPending
      ? getCommentId(latestPending)
      : getCommentId(latestComment);
    const revisionId = String(displayComment.revisionId);

    let uploadedUrl = null;
    if (addressed) {
      const latestAddressed = [...comments]
        .filter((c) =>
          isCommentAddressed(img, c, addressedUploads, entry.imageId)
        )
        .sort(sortByNewest)[0];
      if (latestAddressed) {
        const uploadInfo =
          addressedUploads[
            getUploadKey(entry.imageId, getCommentId(latestAddressed))
          ];
        const responseRevision = findResponseRevision(img, latestAddressed);
        uploadedUrl =
          uploadInfo?.url ||
          resolveImageRevisionUrl(img, responseRevision?._id);
      }
    }

    items.push({
      key: String(entry.imageId),
      imageId: entry.imageId,
      commentId: activeCommentId,
      revisionId,
      comments: commentsWithStatus,
      addressed,
      pendingCount: pendingComments.length,
      image: img,
      requestedUrl: resolveImageRevisionUrl(img, revisionId),
      uploadedUrl,
    });
  }

  return items.sort((a, b) => {
    if (a.addressed !== b.addressed) return a.addressed ? 1 : -1;
    const aDate = a.comments?.[0]?.createdAt;
    const bDate = b.comments?.[0]?.createdAt;
    return new Date(bDate || 0) - new Date(aDate || 0);
  });
};

/** True when every editor-review comment on this event has a linked upload. */
export const isEditorReviewComplete = (
  commentedRawPhotos,
  rawImages,
  itemImages,
  addressedUploads
) => {
  let hasComments = false;
  for (const entry of commentedRawPhotos || []) {
    const img =
      rawImages.find((r) => r.id === entry.imageId) ||
      itemImages?.find((r) => r.id === entry.imageId);
    const comments = (entry.comments || []).filter(
      (c) => getCommentId(c) != null && c.revisionId
    );
    if (!comments.length) continue;
    hasComments = true;
    if (
      comments.some(
        (c) => !isCommentAddressed(img, c, addressedUploads, entry.imageId)
      )
    ) {
      return false;
    }
  }
  return hasComments;
};

export const formatStatusLabel = (status) =>
  status
    ?.replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase());

const REVISION_LIFECYCLE_STATUSES = new Set([
  "revisionSent",
  "revisionInProgress",
  "revisionCompleted",
]);

export const isExtrasRawBatch = (item) =>
  String(item?.summary || "")
    .toLowerCase()
    .includes("extras");

export const isPhoneRawBatch = (item) =>
  String(item?.summary || "")
    .toLowerCase()
    .includes("phone");

export const buildPhoneFolderImages = (images) =>
  (images || []).map((img) => ({
    ...img,
    folder: "phone",
    readOnly: true,
  }));

export const getEditedFolderNames = (item) =>
  isPhoneRawBatch(item) ? ["phone", "web", "print"] : ["web", "print"];

export const hasExtrasEditorReviewForEvent = (marketing, eventId) =>
  (marketing?.rawPhotosComments || []).some(
    (e) =>
      String(e.eventId) === String(eventId) &&
      ((e.paidRevisions || []).length > 0 ||
        (e.comments || []).some((c) => c.revisionId)),
  );

/** Keep grid thumbnails on the agent-requested revision, not activeRevision. */
const sortCommentsNewest = (a, b) =>
  new Date(b.createdAt || 0) - new Date(a.createdAt || 0);

/** Comment still awaiting an editor upload for this revision request. */
export const isRevisionCommentPending = (comment) => {
  if (!comment?.revisionId || getCommentId(comment) == null) return false;
  if (comment.responseRevisionId) return false;
  return !["editorUploaded", "revisionCompleted"].includes(comment.status);
};

export const hasPendingEditorReviewComments = (marketing, eventId) => {
  if (!marketing?.rawPhotosComments?.length || !eventId) return false;
  return marketing.rawPhotosComments.some(
    (entry) =>
      String(entry.eventId) === String(eventId) &&
      (entry.comments || []).some(isRevisionCommentPending)
  );
};

export const effectiveRawBatchStatus = (item, eventStatus, marketing) => {
  if (!item) return item?.status;
  if (
    marketing &&
    item.eventId &&
    hasPendingEditorReviewComments(marketing, item.eventId)
  ) {
    return "revisionSent";
  }
  if (
    marketing &&
    item.eventId &&
    hasExtrasEditorReviewForEvent(marketing, item.eventId) &&
    item.status === "completed" &&
    REVISION_LIFECYCLE_STATUSES.has(eventStatus)
  ) {
    return eventStatus;
  }
  if (
    marketing &&
    item.eventId &&
    hasExtrasEditorReviewForEvent(marketing, item.eventId) &&
    item.status === "completed"
  ) {
    return "revisionSent";
  }
  return item.status;
};
